import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useLanguage } from "../context/LanguageContext";
import { useAddress } from "../context/AddressContext"; 
import { useLoading } from "../context/LoadingContext";
import "../styles/Menu.css";

 
const FIREBASE_FUNCTION_URL = 'https://us-central1-charging-station-bdcf4.cloudfunctions.net';

 
const dataCache = new Map();
//const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const CACHE_DURATION = 0;

const submittedReviewKeys = new Set();
const submittedReviewDetails = new Map();
const reviewRequestIds = new Map();
function getReviewRequestId(key) {
  if (reviewRequestIds.has(key)) return reviewRequestIds.get(key);
  let id;
  try { id = window.localStorage.getItem(key + ':requestId'); } catch {}
  if (!id) id = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  reviewRequestIds.set(key, id);
  try { window.localStorage.setItem(key + ':requestId', id); } catch {}
  return id;
}

function hasCachedReview(key) {
  if (submittedReviewKeys.has(key)) return true;
  try {
    return window.localStorage.getItem(key) === "submitted";
  } catch {
    return false;
  }
}

function readCachedReview(key) {
  if (submittedReviewDetails.has(key)) return submittedReviewDetails.get(key);
  try {
    const saved = JSON.parse(window.localStorage.getItem(key + ":details"));
    return typeof saved?.review === "string" && Number.isInteger(saved.rating)
      && saved.rating >= 1 && saved.rating <= 5 ? saved : null;
  } catch {
    return null;
  }
}

function cacheSubmittedReview(key, details) {
  submittedReviewKeys.add(key);
  submittedReviewDetails.set(key, details);
  try {
    window.localStorage.setItem(key + ":details", JSON.stringify(details));
    window.localStorage.setItem(key, "submitted");
  } catch {
    
  }
}

export default function Menu() {
  const { storeId, tableNo } = useParams();
  const { showLoading, hideLoading } = useLoading();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const { addressData: globalAddressData, setAddressData, setIsTKWAY: setGlobalIsTKWAY } = useAddress(); 
  
  const [storeData, setStoreData] = useState({
    themeColor: "crimson",
    storeName: "Loading..."
  });
  const [categories, setCategories] = useState([]);
  const [foods, setFoods] = useState({}); 
  const [currentCategory, setCurrentCategory] = useState(null);
  const [cart, setCart] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showPreviousInfoPrompt, setShowPreviousInfoPrompt] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: "",
    address: "",
    phone: ""
  });
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationPermission, setLocationPermission] = useState(null); 
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [tempAddressData, setTempAddressData] = useState(null);
  const [isTKWAY, setIsTKWAYLocal] = useState(false);
  const [isAddressSubmitted, setIsAddressSubmitted] = useState(false);
  const [loadingImages, setLoadingImages] = useState({});
  
  const [showOrderDrawer, setShowOrderDrawer] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [placedOrderItems, setPlacedOrderItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [confirmedOrders, setConfirmedOrders] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [reviewRating, setReviewRating] = useState(0);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const reviewCacheKey = "menu:review:v1:" + JSON.stringify([storeId, tableNo]);
  const [reviewSubmitted, setReviewSubmitted] = useState(() => hasCachedReview(reviewCacheKey));
  const reviewRequest = useRef(null);
  const reviewButtonRef = useRef(null);
  const reviewDragRef = useRef(null);
  const suppressReviewClick = useRef(false);
  const [reviewButtonPosition, setReviewButtonPosition] = useState(null);
  const [isDraggingReview, setIsDraggingReview] = useState(false);

  function clampReviewPosition(left, top, width, height) {
    const viewport = window.visualViewport;
    const minLeft = (viewport?.offsetLeft || 0) + 8;
    const minTop = (viewport?.offsetTop || 0) + 8;
    const maxLeft = Math.max(minLeft, minLeft + (viewport?.width || window.innerWidth) - width - 16);
    const maxTop = Math.max(minTop, minTop + (viewport?.height || window.innerHeight) - height - 16);
    return {
      left: Math.max(minLeft, Math.min(left, maxLeft)),
      top: Math.max(minTop, Math.min(top, maxTop))
    };
  }

  function startReviewDrag(event) {
    if (!event.isPrimary || event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    suppressReviewClick.current = false;
    reviewDragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX, y: event.clientY,
      left: rect.left, top: rect.top,
      width: rect.width, height: rect.height,
      moved: false
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function moveReviewDrag(event) {
    const drag = reviewDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved && Math.hypot(dx, dy) < 6) return;
    drag.moved = true;
    suppressReviewClick.current = true;
    setIsDraggingReview(true);
    setReviewButtonPosition(clampReviewPosition(
      drag.left + dx, drag.top + dy, drag.width, drag.height
    ));
  }

  function endReviewDrag(event) {
    const drag = reviewDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    reviewDragRef.current = null;
    setIsDraggingReview(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  useEffect(() => {
    const keepButtonVisible = () => {
      const button = reviewButtonRef.current;
      if (!button) return;
      const rect = button.getBoundingClientRect();
      setReviewButtonPosition(position => {
        if (!position) return position;
        const next = clampReviewPosition(position.left, position.top, rect.width, rect.height);
        return next.left === position.left && next.top === position.top ? position : next;
      });
    };
    keepButtonVisible();
    window.addEventListener("resize", keepButtonVisible);
    window.visualViewport?.addEventListener("resize", keepButtonVisible);
    window.visualViewport?.addEventListener("scroll", keepButtonVisible);
    return () => {
      window.removeEventListener("resize", keepButtonVisible);
      window.visualViewport?.removeEventListener("resize", keepButtonVisible);
      window.visualViewport?.removeEventListener("scroll", keepButtonVisible);
    };
  }, [language, reviewSubmitted, showReviewForm]);

  const hasConfirmedOrders = confirmedOrders.length > 0;
  const canWriteReview = hasConfirmedOrders && !reviewSubmitted && !hasCachedReview(reviewCacheKey);

  useEffect(() => {
    setConfirmedOrders([]);
    setShowReviewForm(false);
    const savedReview = readCachedReview(reviewCacheKey);
    setReviewText(savedReview?.review || "");
    setReviewRating(savedReview?.rating || 0);
    setReviewError("");
    setReviewSubmitted(hasCachedReview(reviewCacheKey));
    setIsSubmittingReview(false);
    reviewRequest.current = null;
    const handleStorage = (event) => {
      if (event.key === reviewCacheKey && event.newValue === "submitted") {
        setReviewSubmitted(true);
        const savedReview = readCachedReview(reviewCacheKey);
        setReviewText(savedReview?.review || "");
        setReviewRating(savedReview?.rating || 0);
        setReviewError("");
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      reviewRequest.current = null;
      window.removeEventListener("storage", handleStorage);
    };
  }, [storeId, tableNo, reviewCacheKey]);

  async function handleReviewSubmit(event) {
    event.preventDefault();
    if (reviewRequest.current || reviewSubmitted || !canWriteReview) return;
    const review = reviewText.trim();
    if (!review || review.length > 1000 || !Number.isInteger(reviewRating) || reviewRating < 1 || reviewRating > 5) {
      setReviewError(language === "cn"
        ? "Enter a review (up to 1000 characters) and select a rating from 1 to 5."
        : "请输入评价（最多1000字），并选择1至5分的评分。");
      return;
    }


    const request = {};
    reviewRequest.current = request;
     
    setIsSubmittingReview(true);
    setReviewError("");
    try {
      const result = await callFirebaseFunction('addreview', {
        storeId, tableno: tableNo, review, rating: reviewRating,
        requestId: getReviewRequestId(reviewCacheKey)
      }, false);
      if (result?.success !== true) {
        throw new Error("Review was not saved");
      }
      
      const saved = result.data || { review, rating: reviewRating };
      cacheSubmittedReview(reviewCacheKey, saved);
      if (reviewRequest.current !== request) return;
      setReviewText(saved.review);
      setReviewRating(saved.rating);
      setReviewSubmitted(true);
    } catch (error) {
      if (reviewRequest.current !== request) return;
      setReviewError(language === "cn"
        ? "Could not submit your review. Please try again."
        : "评价提交失败，请重试。");
    } finally {
      if (reviewRequest.current === request) {
        reviewRequest.current = null;
        setIsSubmittingReview(false);
      }
    }
  }

  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  const [deletedItems, setDeletedItems] = useState([]);
  const [modifiedQuantities, setModifiedQuantities] = useState({});
 
  const [zoomedImage, setZoomedImage] = useState(null);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [tempInstructions, setTempInstructions] = useState("");

  const [storeStatus, setStoreStatus] = useState(null);
  const cartRequest = useRef(false);
  const [isRefreshingDrawer, setIsRefreshingDrawer] = useState(false);
  const [drawerRefreshError, setDrawerRefreshError] = useState("");
  const ordersReadVersion = useRef(0);
  const cartReadVersion = useRef(0);
  useEffect(() => () => { cartReadVersion.current += 1; }, [storeId, tableNo]);

 
  const callFirebaseFunction = useCallback(async (action, data, useCache = true) => {
    const cacheKey = `${action}_${JSON.stringify(data)}`;
    
 
    if (useCache) {
      const cached = dataCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        console.log(`[CACHE] Using cached data for ${action}`);
        return cached.data;
      }
    }
    
    try {
      const response = await fetch(`${FIREBASE_FUNCTION_URL}/foodManager`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, data })
      });
      const result = await response.json();
      if (!response.ok || result.success !== true) throw new Error(result.error || "Request failed");
      
      
      if (useCache && result.success) {
        dataCache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
      }
      
      return result;
    } catch (error) {
      console.error(`Error calling ${action}:`, error);
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!storeId || !tableNo) {
      setError("Invalid parameters. Please scan again.");
      return;
    }

    const containsTKWAY = tableNo && tableNo.toUpperCase().includes("TKWAY");
    setIsTKWAYLocal(containsTKWAY);
    setGlobalIsTKWAY(containsTKWAY);  
    
    if (containsTKWAY) {
      if (globalAddressData && globalAddressData.name && globalAddressData.address && globalAddressData.phone) {
        setShowPreviousInfoPrompt(true);
      } else {
        setShowAddressForm(true);
        setIsAddressSubmitted(false);
      }
    }

    checkLocationPermission();
    loadInitialData();
  }, [storeId, tableNo]);

  const loadInitialData = async () => {
    const startTime = performance.now();
    
    try {
      setLoading(true);
      
      
      const [storeResult, categoriesResult] = await Promise.all([
        callFirebaseFunction('getStore', { storeId }).catch(err => {
          console.error("Store load failed:", err);
          return null;
        }),
        callFirebaseFunction('getCategories', { storeId, tableno: tableNo }).catch(err => {
          console.error("Categories load failed:", err);
          return null;
        })
      ]);
      
      
      if (storeResult?.success) {
        const storeDataResult = storeResult.data;
        setStoreStatus(storeDataResult.status || "open");
        setStoreData({
          themeColor: storeDataResult.themeColor || "crimson",
          storeName: storeDataResult.storeName || storeDataResult.name || "Restaurant",
          storeNamecn: storeDataResult.storeNamecn || storeDataResult.namecn || "Restaurant"
        });
      }
      
      
      if (categoriesResult?.success) {
        const categoryList = categoriesResult.data || [];
        setCategories(categoryList);
        setFoods({});
        setCurrentCategory(categoryList[0]?.id || "");
        
        if (categoryList.length > 0) {
        
          const foodPromises = categoryList.map(category => 
            loadSingleCategoryFoods(category.id)
          );
          
          const allFoodsResults = await Promise.all(foodPromises);
          
          const foodsObj = {};
          categoryList.forEach((category, index) => {
            foodsObj[category.id] = allFoodsResults[index] || [];
          });
          
          setFoods(foodsObj);
          setCurrentCategory(categoryList[0].id);
        }
      }
      
  
      Promise.all([
        loadCartItems(),
        fetchConfirmedOrders()
      ]).catch(err => console.error("Background load error:", err));
      
      const endTime = performance.now();
      console.log(`[PERF] Initial load completed in ${(endTime - startTime).toFixed(0)}ms`);
      
    } catch (err) {
      console.error("Load error:", err);
      setError("Failed to load data. Please refresh.");
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  const extractResponseData = (response) => {
    if (response?.success && response?.data) return response.data;
    if (response?.data) return response.data;
    return null;
  };

  async function loadCartItems(reportError = false) {
    const version = ++cartReadVersion.current;
    try {
      const res = await callFirebaseFunction('getCartItems', { storeId, tableno: tableNo }, false);
      if (version !== cartReadVersion.current) return [];
      const allItems = (extractResponseData(res) || []).map(item => ({ ...item, quantity: Number(item.quantity) || 0, price: Number(item.price) || 0 }));
      const activeItems = allItems.filter(i => i.status === "active").map(item => ({
        ...item, id: item.cartId || item.id || item._id
      }));
      if (activeItems.some(item => !item.id) || new Set(activeItems.map(item => item.id)).size !== activeItems.length) {
        throw new Error('Cart items are missing unique IDs. Please refresh the cart.');
      }
      const filteredItems = activeItems.filter(item => !deletedItems.includes(item.id || item._id));
      const itemsWithModifiedQty = filteredItems.map(item => {
        const itemId = item.id || item._id;
        if (modifiedQuantities[itemId] !== undefined) {
          return { ...item, quantity: modifiedQuantities[itemId], _id: itemId };
        }
        return { ...item, _id: itemId };
      });
      setCartItems(itemsWithModifiedQty);
      return itemsWithModifiedQty;
    } catch (error) {
      console.error("Error loading cart items:", error);
      if (reportError) throw error;
      if (version === cartReadVersion.current) alert(error.message);
      return [];
    }
  }

  async function fetchConfirmedOrders(reportError = false) {
    const version = ++ordersReadVersion.current;
    try {
      const res = await callFirebaseFunction('getOrders', { storeId, tableno: tableNo }, false);
      if (version !== ordersReadVersion.current) return;
      const ordersData = extractResponseData(res);
      if (!ordersData) return;
      const orders = (ordersData || []).map(i => ({
        _id: i.orderId || i.id || i._id,
        itemname: i.itemname,
        itemnamecn: i.itemnamecn || '',
        quantity: i.quantity,
        price: i.price,
        status: i.status || 'confirmed'
      }));
      setConfirmedOrders(orders);
    } catch (err) {
      console.error("Error fetching orders:", err);
      if (reportError) throw err;
    }
  }

  async function refreshDrawer() {
    if (cartRequest.current) return;
    cartRequest.current = true;
    setIsProcessingOrder(true);
    setIsRefreshingDrawer(true);
    setDrawerRefreshError("");
    try {
      
      const results = await Promise.allSettled([loadCartItems(true), fetchConfirmedOrders(true)]);
      if (results.some(result => result.status === 'rejected')) {
        setDrawerRefreshError(language === 'cn' ? 'Could not refresh all items. Please try again.' : '部分商品刷新失败，请重试。');
      }
    } finally {
      cartRequest.current = false;
      setIsProcessingOrder(false);
      setIsRefreshingDrawer(false);
    }
  }

  async function loadSingleCategoryFoods(categoryId) {
    try {
      const res = await callFirebaseFunction('getFoods', { storeId, categoryId });
      let foodList = extractResponseData(res) || [];
      if (!Array.isArray(foodList)) foodList = [];
      
   
      const fileList = foodList.map(f => f.image).filter(Boolean);
      if (fileList.length > 0) {
        try {
          const convertRes = await callFirebaseFunction('convertImages', { fileList });
          const convertData = extractResponseData(convertRes);
          if (convertData && Array.isArray(convertData)) {
            const tempUrlsMap = {};
            convertData.forEach(item => {
              tempUrlsMap[item.fileID] = item.tempFileURL || item.fileID;
            });
            foodList = foodList.map(f => ({ ...f, image: tempUrlsMap[f.image] || f.image }));
          }
        } catch (err) {
          console.error("Image conversion error:", err);
        }
      }
      return foodList;
    } catch (error) {
      console.error("Foods load error:", error);
      return [];
    }
  }

  async function loadFoods(categoryId) {
    setLoadingImages(prev => ({ ...prev, [categoryId]: true }));
    try {
      const foods = await loadSingleCategoryFoods(categoryId);
      setFoods(prev => ({ ...prev, [categoryId]: foods }));
    } catch (error) {
      console.error("Error loading foods:", error);
    } finally {
      setLoadingImages(prev => ({ ...prev, [categoryId]: false }));
    }
  }
 
  const increase = (food) => {
    setCart((c) => {
      const currentQty = c[food.id]?.quantity || 0;
      return { ...c, [food.id]: { ...food, quantity: currentQty + 1 } };
    });
  };

  const decrease = (food) => {
    setCart((c) => {
      const currentQty = c[food.id]?.quantity || 0;
      if (currentQty <= 1) {
        const newCart = { ...c };
        delete newCart[food.id];
        return newCart;
      }
      return { ...c, [food.id]: { ...food, quantity: currentQty - 1 } };
    });
  };

  async function selectCategory(categoryId) {
    setCurrentCategory(categoryId);
    if (!foods[categoryId] || foods[categoryId].length === 0) {
      await loadFoods(categoryId);
    }
  }

  const handleImageClick = (imageUrl, foodName) => {
    setZoomedImage({ url: imageUrl, name: foodName });
  };

  const closeZoomedImage = () => {
    setZoomedImage(null);
  };

  const handleInstructionsClick = () => {
    setTempInstructions(instructions);
    setShowInstructionsModal(true);
  };

  const handleInstructionsSave = () => {
    setInstructions(tempInstructions);
    setShowInstructionsModal(false);
  };

  const handleInstructionsCancel = () => {
    setTempInstructions(instructions);
    setShowInstructionsModal(false);
  };

  function removeCartItem(cartId) {
    if (cartRequest.current) return;
    cartReadVersion.current += 1;
    setDeletedItems(prev => [...new Set([...prev, cartId])]);
    setModifiedQuantities(prev => { const next = { ...prev }; delete next[cartId]; return next; });
    setCartItems(prev => prev.filter(item => item._id !== cartId));
  }

  function changeCartItemQty(cartId, diff) {
    if (cartRequest.current) return;
    const item = cartItems.find(i => i._id === cartId);
    if (!item) return;
    const quantity = Number(item.quantity) + diff;
    if (quantity <= 0) { removeCartItem(cartId); return; }
    cartReadVersion.current += 1;
    setModifiedQuantities(prev => ({ ...prev, [cartId]: quantity }));
    setCartItems(prev => prev.map(i => i._id === cartId ? { ...i, quantity } : i));
  }

  async function batchUpdateToServer() {
    if (!deletedItems.length && !Object.keys(modifiedQuantities).length) return;
    await callFirebaseFunction('batchUpdateAndDelete', {
      storeId, tableno: tableNo, deletedIds: deletedItems,
      items: Object.entries(modifiedQuantities).map(([cartId, quantity]) => ({ cartId, quantity }))
    }, false);
    setDeletedItems([]);
    setModifiedQuantities({});
  }

  async function addAllToCartAtOnce() {
    if (!canAddToCart()) {
      const message = language === "cn" ? "Please fill in name, address, and phone first" : "请先填写姓名、地址和电话";
      alert(message);
      setShowAddressForm(true);
      return false;
    }

    const itemsToAdd = Object.values(cart)
      .filter(item => item.quantity > 0)
      .map(item => ({
        id: item.id, name: item.name, namecn: item.namecn, price: item.price, quantity: item.quantity
      }));

    if (itemsToAdd.length === 0) return false;

    try {
      const response = await callFirebaseFunction('batchAddToCart', {
        storeId,
        tableno: tableNo,
        items: itemsToAdd
      }, false);

      if (response.success) {
        setCart({});
        setPlacedOrderItems(itemsToAdd);
        setOrderPlaced(true);
      
        return true;
      } else {
        throw new Error(response.error || "Unknown error");
      }
    } catch (error) {
      console.error("Batch add error:", error);
      alert(language === "cn" ? "Error adding items to cart" : "一键加入购物车失败");
      return false;
    }
  }

  const handleCartIconClick = async () => {
    if (cartRequest.current) return;
    if (showOrderDrawer) {
      await confirmOrderFromDrawer();
      return;
    }
    
    cartRequest.current = true;
    try {
    const itemsToAdd = Object.values(cart).filter(item => item.quantity > 0);
    
    if (itemsToAdd.length > 0) {
      setShowOrderDrawer(true);
      setIsProcessingOrder(true);
      setOrderPlaced(false);
      
      
      await Promise.all([loadCartItems(), fetchConfirmedOrders()]);
      
      const success = await addAllToCartAtOnce();
      
      if (success) {
        await Promise.all([loadCartItems(), fetchConfirmedOrders()]);
      }
      
      setIsProcessingOrder(false);
    } else {
      setShowOrderDrawer(true);
      setIsProcessingOrder(true);
      setOrderPlaced(false);
      
      await Promise.all([loadCartItems(), fetchConfirmedOrders()]);
    }
    } finally { cartRequest.current = false; setIsProcessingOrder(false); }
  };

  const closeDrawerWithoutSync = () => {
    setShowOrderDrawer(false);
    setOrderPlaced(false);
    setPlacedOrderItems([]);
    setIsProcessingOrder(false);
  };

  const closeOrderDrawer = async () => {
    if (cartRequest.current) return;
    cartRequest.current = true;
    setIsProcessingOrder(true);
    try {
      cartReadVersion.current += 1;
      await batchUpdateToServer();
      closeDrawerWithoutSync();
    } catch (error) {
      alert(language === "cn" ? "Could not save cart changes. Please retry." : "购物车修改保存失败，请重试。");
    } finally { cartRequest.current = false; setIsProcessingOrder(false); }
  };

 
  async function confirmOrderFromDrawer() {
    if (cartRequest.current) return;
    if (cartItems.length === 0) {
      alert(language === "cn" ? "No items in cart to confirm" : "购物车中没有可确认的商品");
      return;
    }
    
    cartRequest.current = true;
    setIsProcessingOrder(true);
    showLoading(language === "cn" ? "Confirming order..." : "订单确认中...");
    try {
      cartReadVersion.current += 1;
      await batchUpdateToServer();

     
      const result = await callFirebaseFunction('confirmOrder', {
        storeId,
        tableno: tableNo,
        comment: instructions,
        ...(isTKWAY ? { usrname: tempAddressData?.name, address: tempAddressData?.address, phone: tempAddressData?.phone } : {})
      }, false);

      if (result.success) {
        console.log('Order confirmed:', result.data);
        
       
        cartReadVersion.current += 1;
        setCartItems([]);
        setDeletedItems([]);
        setModifiedQuantities({});
        setInstructions("");
        
        
        await fetchConfirmedOrders();
        
        closeDrawerWithoutSync();
        alert(language === "cn" ? "Order confirmed!" : "订单已确认!");
      } else {
        throw new Error(result.error || "Failed to confirm order");
      }
    } catch (err) {
      console.error("Confirm error:", err);
      alert(language === "cn" ? "Confirm failed" : "订单确认失败");
    } finally {
      cartRequest.current = false;
      setIsProcessingOrder(false);
      hideLoading();
    }
  }

  const handleUsePreviousInfo = () => {
    if (globalAddressData) {
      setAddressForm(globalAddressData);
      setTempAddressData(globalAddressData);
      setIsAddressSubmitted(true);
      setShowAddressForm(false);
      setShowPreviousInfoPrompt(false);
    }
  };

  const handleEnterNewInfo = () => {
    setShowPreviousInfoPrompt(false);
    setShowAddressForm(true);
    setIsAddressSubmitted(false);
  };

  const checkLocationPermission = () => {
    if (!navigator.permissions || !navigator.permissions.query) {
      setLocationPermission('unknown');
      return;
    }
    navigator.permissions.query({ name: 'geolocation' })
      .then(permissionStatus => {
        setLocationPermission(permissionStatus.state);
        permissionStatus.onchange = () => {
          setLocationPermission(permissionStatus.state);
          if (permissionStatus.state === 'granted') setShowLocationPrompt(false);
        };
      })
      .catch(() => setLocationPermission('unknown'));
  };

  const requestLocationPermission = () => {
    setGettingLocation(true);
    setShowLocationPrompt(false);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocationPermission('granted');
        setGettingLocation(false);
        getAddressFromCoordinates(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        setGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermission('denied');
          showLocationInstructions();
        } else {
          alert(language === "cn" ? "Unable to get your location." : "无法获取您的位置。");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const showLocationInstructions = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      const message = language === "cn" 
        ? "请在设置中允许浏览器访问您的位置:\n\n1. 打开设置\n2. 找到 Safari/Chrome\n3. 点击位置\n4. 选择允许\n\n或者直接手动输入地址。"
        : "Please enable location access in settings:\n\n1. Open Settings\n2. Find Safari/Chrome\n3. Tap on Location\n4. Select Allow\n\nOr enter address manually.";
      alert(message);
    } else {
      alert(language === "cn" ? "请在浏览器设置中允许位置访问，或手动输入地址。" : "Please allow location access in browser settings, or enter address manually.");
    }
  };

  const getAddressFromCoordinates = async (latitude, longitude) => {
    const coordinates = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    setAddressForm(prev => ({ ...prev, address: coordinates }));
    alert(language === "cn" ? `Location captured: ${coordinates}` : `位置获取成功: ${coordinates}`);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert(language === "cn" ? "Geolocation is not supported by your browser" : "您的浏览器不支持地理位置");
      return;
    }
    if (locationPermission === 'denied') { showLocationInstructions(); return; }
    if (locationPermission === 'prompt' || locationPermission === 'unknown') { setShowLocationPrompt(true); return; }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGettingLocation(false);
        getAddressFromCoordinates(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        setGettingLocation(false);
        handleLocationError(error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleLocationError = (error) => {
    let errorMessage = language === "cn" ? "Error getting location" : "获取位置失败";
    switch(error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = language === "cn" ? "Location permission denied." : "位置权限被拒绝。";
        setLocationPermission('denied');
        showLocationInstructions();
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = language === "cn" ? "Location information unavailable" : "位置信息不可用";
        break;
      case error.TIMEOUT:
        errorMessage = language === "cn" ? "Location request timed out" : "位置请求超时";
        break;
      default:
        break;
    }
    alert(errorMessage);
  };

  const handleAddressInputChange = (field, value) => {
    setAddressForm(prev => ({ ...prev, [field]: value }));
  };

  const isAddressFormValid = () => {
    return addressForm.name.trim() !== "" && addressForm.address.trim() !== "" && addressForm.phone.trim() !== "";
  };

  const handleContinue = async () => {
    if (!isAddressFormValid()) return;
    showLoading(language === "cn" ? "Saving address..." : "保存地址中...");
    try {
      await callFirebaseFunction('saveTableAddress', { storeId, tableno: tableNo, addressData: addressForm }, false);
      setTempAddressData(addressForm);
      setAddressData(addressForm);
      setIsAddressSubmitted(true);
      setShowAddressForm(false);
    } catch (error) {
      alert(language === "cn" ? "Could not save address. Please retry." : "地址保存失败，请重试。");
    } finally { hideLoading(); }
  };

  const handleCancel = () => {
    setAddressForm({ name: "", address: "", phone: "" });
    setTempAddressData(null);
    setIsAddressSubmitted(false);
    setShowAddressForm(false);
  };

  const canAddToCart = () => {
    if (!isTKWAY) return true;
    return isAddressSubmitted && tempAddressData !== null;
  };

  const currentFoods = foods[currentCategory] || [];
  const isLoadingCurrentCategory = loadingImages[currentCategory];
  
  const totalSelectedItems = Object.values(cart).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const cartTotal = Object.values(cart).reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartItemsTotal = cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const cartItemsCount = cartItems.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
  const confirmedTotal = confirmedOrders.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const hasItemsInCart = totalSelectedItems > 0 || cartItemsCount > 0;

  if (loading) return <div className="page">Loading...</div>;
  if (error) return <div className="page">{error}</div>;
  if (storeStatus === 'closed') return <div className="store-closed-message">
    <h1>{storeData.storeName}</h1><p>{storeData.storeNamecn}</p>
    <h2>{language === "cn" ? "服务暂时关闭" : "The service is closed for the moment"}</h2>
  </div>;

  return (
    <div className="premium-page">
      <div className="premium-sidebar">
        <div className="brand-section">
          <h1>{storeData.storeName}</h1>
          <p>{storeData.storeNamecn}</p>
        </div>
        <div className="category-list">
          {categories.map((c) => (
            <button
              type="button"
              aria-pressed={c.id === currentCategory}
              key={c.id}
              className={`premium-category ${c.id === currentCategory ? "active" : ""}`}
              onClick={() => selectCategory(c.id)}
            >
              {language === "cn" ? c.namecn : c.name}
            </button>
          ))}
        </div>
      </div>

      <div className="premium-main">
        <div className="premium-topbar">
          <div className="hero-title">
            <h2>Good food, <span>for every mood</span></h2>
          </div>
          <button className="lang-switch premium-lang" onClick={() => setLanguage((l) => (l === "cn" ? "en" : "cn"))}>
            {language === "cn" ? "中文" : "EN"}
          </button>
        </div>

      
        {showPreviousInfoPrompt && (
          <div className="address-form-overlay">
            <div className="address-form-container prompt-container">
              <h3>{language === "cn" ? "Welcome Back!" : "欢迎回来！"}</h3>
              <p>{language === "cn" ? "Use previous info?" : "使用之前的信息？"}</p>
              {globalAddressData && (
                <div className="previous-info-summary">
                  <p>{globalAddressData.name} - {globalAddressData.phone}</p>
                  <p>{globalAddressData.address}</p>
                </div>
              )}
              <div className="prompt-buttons">
                <button onClick={handleUsePreviousInfo}>{language === "cn" ? "Yes" : "是"}</button>
                <button onClick={handleEnterNewInfo}>{language === "cn" ? "No" : "否"}</button>
              </div>
            </div>
          </div>
        )}

        {showAddressForm && (
          <div className="address-form-overlay">
            <div className="address-form-container">
              <h3>{language === "cn" ? "Delivery Information" : "配送信息"}</h3>
              <input
                type="text"
                placeholder={language === "cn" ? "Name" : "姓名"}
                value={addressForm.name}
                onChange={(e) => handleAddressInputChange('name', e.target.value)}
              />
              <div className="address-input-wrapper">
                <input
                  type="text"
                  placeholder={language === "cn" ? "Address" : "地址"}
                  value={addressForm.address}
                  onChange={(e) => handleAddressInputChange('address', e.target.value)}
                />
                <button onClick={getCurrentLocation} disabled={gettingLocation}>
                  {gettingLocation ? "⌛" : "📍"}
                </button>
              </div>
              <input
                type="tel"
                placeholder={language === "cn" ? "Phone" : "电话"}
                value={addressForm.phone}
                onChange={(e) => handleAddressInputChange('phone', e.target.value)}
              />
              {showLocationPrompt && (
                <div className="location-prompt">
                  <p>{language === "cn" ? "Allow location access?" : "允许位置访问？"}</p>
                  <button onClick={requestLocationPermission}>{language === "cn" ? "Allow" : "允许"}</button>
                  <button onClick={() => setShowLocationPrompt(false)}>{language === "cn" ? "Skip" : "跳过"}</button>
                </div>
              )}
              <div className="address-form-buttons">
                <button onClick={handleCancel}>{language === "cn" ? "Cancel" : "取消"}</button>
                <button onClick={handleContinue} disabled={!isAddressFormValid()}>
                  {language === "cn" ? "Continue" : "继续"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="premium-food-grid">
          {isLoadingCurrentCategory ? (
            <div className="loading-items"><div className="spinner"></div></div>
          ) : (
            currentFoods.map((f) => (
              <div key={f.id} className="premium-food-card">
                <img 
                  src={f.image || "/default-food.jpg"} 
                  alt={f.name} 
                  className="premium-food-image"
                  onClick={() => handleImageClick(f.image || "/default-food.jpg", language === "cn" ? f.namecn : f.name)}
                />
                <div className="premium-food-content">
                  <div className="premium-food-details">
                    <h3>{language === "cn" ? f.namecn : f.name}</h3>
                    <p>Rs. {f.price}</p>
                  </div>
                  <div className="premium-qty-control">
                    <button onClick={() => decrease(f)}>−</button>
                    <span>{cart[f.id]?.quantity || 0}</span>
                    <button onClick={() => increase(f)}>+</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {hasConfirmedOrders && (
          <section className="premium-review-section">
            {showReviewForm && (
              <form
                onSubmit={handleReviewSubmit}
                aria-busy={isSubmittingReview}
                id="order-review-form"
                className="premium-review-panel"
              >
                <div className="premium-review-header">
                  <h3>{language === "cn" ? "Your review" : "您的评价"}</h3>
                  <button
                    type="button"
                    className="premium-order-btn"
                    onClick={() => setShowReviewForm(false)}
                  >
                    {language === "cn" ? "Close" : "关闭"}
                  </button>
                </div>
                {reviewSubmitted && !reviewText && (
                  <p role="status">
                    {language === "cn"
                      ? "Your review was submitted previously, but its text and rating were not saved by the earlier version."
                      : "您的评价此前已提交，但旧版本未保存评价内容和评分。"}
                  </p>
                )}
                <fieldset className="premium-review-rating" disabled={isSubmittingReview || reviewSubmitted}>
                  <legend className="premium-review-legend">
                    {language === "cn" ? "Rating out of 5" : "评分（满分5分）"}
                  </legend>
                  <div className="premium-review-options">
                    {[1, 2, 3, 4, 5].map(value => (
                      <label key={value} className="premium-review-option">
                        <input
                          type="radio"
                          name="order-review-rating"
                          value={value}
                          checked={reviewRating === value}
                          onChange={() => setReviewRating(value)}
                        />
                        <span className={value <= reviewRating ? "premium-review-star is-selected" : "premium-review-star"}>
                          {value} ★
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label htmlFor="order-review-text" className="premium-review-label">
                  {language === "cn" ? "Your review" : "您的评价"}
                </label>
                <textarea
                  id="order-review-text"
                  disabled={isSubmittingReview || reviewSubmitted}
                  autoFocus
                  rows={4}
                  maxLength={1000}
                  value={reviewText}
                  onChange={event => setReviewText(event.target.value)}
                  placeholder={language === "cn" ? "Tell us about your experience..." : "请分享您的用餐体验..."}
                  className="premium-review-textarea"
                />
                {reviewError && (
                  <p className="premium-review-error" role="alert">{reviewError}</p>
                )}
                {reviewSubmitted ? (
                  <p className="premium-review-success" role="status">
                    {language === "cn" ? "Thank you! Your review has been submitted." : "感谢您！您的评价已提交。"}
                  </p>
                ) : (
                  <button
                    type="submit"
                    className="premium-order-btn premium-review-submit"
                    disabled={isSubmittingReview || !reviewText.trim() || reviewRating < 1}
                  >
                    {isSubmittingReview && <span className="premium-review-spinner" aria-hidden="true" />}
                    <span aria-live="polite">
                      {isSubmittingReview
                        ? (language === "cn" ? "Submitting..." : "提交中...")
                        : (language === "cn" ? "Submit review" : "提交评价")}
                    </span>
                  </button>
                )}
              </form>
            )}
            <button
              type="button"
              ref={reviewButtonRef}
              aria-expanded={showReviewForm}
              aria-controls="order-review-form"
              title="Tap to open or close; drag to reposition"
              onPointerDown={startReviewDrag}
              onPointerMove={moveReviewDrag}
              onPointerUp={endReviewDrag}
              onPointerCancel={endReviewDrag}
              onLostPointerCapture={endReviewDrag}
              style={reviewButtonPosition ? {
                position: "fixed",
                left: reviewButtonPosition.left,
                top: reviewButtonPosition.top,
                right: "auto",
                bottom: "auto",
                margin: 0,
                maxWidth: "calc(100vw - 16px)"
              } : undefined}
              onClick={(event) => {
                if (suppressReviewClick.current && event.detail !== 0) {
                  suppressReviewClick.current = false;
                  event.preventDefault();
                  return;
                }
                suppressReviewClick.current = false;
                if (hasCachedReview(reviewCacheKey)) {
                  const savedReview = readCachedReview(reviewCacheKey);
                  setReviewSubmitted(true);
                  setReviewText(savedReview?.review || "");
                  setReviewRating(savedReview?.rating || 0);
                }
                setShowReviewForm(visible => !visible);
              }}
              className={`premium-review-btn${isDraggingReview ? " is-dragging" : ""}`}
            >
              <span aria-hidden="true">
                {reviewSubmitted || hasCachedReview(reviewCacheKey) ? "★" : "☆"}
              </span>
              {reviewSubmitted || hasCachedReview(reviewCacheKey)
                ? (language === "cn" ? "View your review" : "查看您的评价")
                : showReviewForm
                  ? (language === "cn" ? "Close review" : "关闭评价")
                  : (language === "cn" ? "Write a review" : "写评价")}
            </button>
          </section>
        )}
        

        <div className="premium-cart-bar-container">
          {hasItemsInCart && (
            <button className="premium-instructions-btn" onClick={handleInstructionsClick}>
              {instructions ? "📝✓" : "📝"}
            </button>
          )}
          <div className="premium-cart-bar">
            <div className="cart-left" onClick={handleCartIconClick}>
              <div className="cart-icon">🛒</div>
              <div className="cart-text">
                <small>
                  {showOrderDrawer ? cartItemsCount : totalSelectedItems + cartItemsCount} {language === "cn" ? "items" : "件"}
                </small>
              </div>
            </div>
            <div className="cart-total">Rs. {(showOrderDrawer ? cartItemsTotal : cartTotal + cartItemsTotal).toFixed(2)}</div>
            <button className="premium-order-btn" disabled={isProcessingOrder} onClick={handleCartIconClick}>
              {showOrderDrawer 
                ? (language === "cn" ? "CONFIRM ORDER" : "确认订单")
                : (language === "cn" ? "VIEW CART" : "查看购物车")}
            </button>
          </div>
        </div>
      </div>

     
      {showOrderDrawer && (
        <>
          <div className="order-drawer-overlay" onClick={closeOrderDrawer}></div>
          <div className="order-drawer">
            <div className="order-drawer-handle"></div>
            <div className="order-drawer-header">
              <h2>{language === "cn" ? "My Cart" : "我的购物车"}</h2>
              <button className="cart-refresh-btn" disabled={isProcessingOrder} aria-label={language === "cn" ? "Refresh cart" : "刷新购物车"} aria-busy={isRefreshingDrawer} onClick={refreshDrawer}>⟳</button>
            </div>
            <div className="order-drawer-content">
              {drawerRefreshError && <p role="alert">{drawerRefreshError}</p>}
              {isProcessingOrder && (
                <div className="drawer-loading">
                  <div className="drawer-spinner"></div>
                  <p>{isRefreshingDrawer ? (language === "cn" ? "Refreshing cart..." : "正在刷新购物车...") : (language === "cn" ? "Updating cart..." : "正在更新购物车...")}</p>
                </div>
              )}
              <h3 className="section-label">
                {language === "cn" ? "Cart Items" : "购物车商品"}
                {cartItems.length > 0 && <span className="item-count">({cartItems.length})</span>}
              </h3>
              {cartItems.length === 0 && !isProcessingOrder ? (
                <div className="empty-cart-message">{language === "cn" ? "No items in cart" : "购物车是空的"}</div>
              ) : (
                <div className="order-items-list">
                  {cartItems.map((item) => (
                    <div key={`cart:${item._id}`} className="order-item cart-item-drawer">
                      <div className="order-item-info">
                        <span className="order-item-name">
                          {language === "cn" ? (item.itemnamecn || item.itemname) : item.itemname}
                        </span>
                        <div className="qty-controls">
                          <button className="qty-btn" disabled={isProcessingOrder} onClick={() => changeCartItemQty(item._id, -1)}>−</button>
                          <span className="qty-value">{item.quantity}</span>
                          <button className="qty-btn" disabled={isProcessingOrder} onClick={() => changeCartItemQty(item._id, 1)}>+</button>
                        </div>
                      </div>
                      <div className="order-item-actions">
                        <span className="order-item-price">Rs. {(item.price * item.quantity).toFixed(2)}</span>
                        <button className="remove-item-btn" disabled={isProcessingOrder} onClick={() => removeCartItem(item._id)}>
                          {language === "cn" ? "Remove" : "删除"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {showOrderDrawer && (
                <div className="order-total-section" data-testid="drawer-cart-total" aria-live="polite">
                  <div className="order-total-row grand-total">
                    <span>{language === "cn" ? "Cart Total" : "购物车合计"}</span>
                    <span>Rs. {cartItemsTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}
              <div className="drawer-divider"></div>
              <h3 className="section-label">
                {language === "cn" ? "Confirmed Orders" : "已确认订单"}
                {confirmedOrders.length > 0 && <span className="item-count">({confirmedOrders.length})</span>}
              </h3>
              {confirmedOrders.length === 0 ? (
                <div className="empty-cart-message">{language === "cn" ? "No confirmed orders" : "没有已确认的订单"}</div>
              ) : (
                <div className="order-items-list">
                  {confirmedOrders.map((item) => (
                    <div key={`confirmed:${item._id}`} className="order-item confirmed-item">
                      <div className="order-item-info">
                        <span className="order-item-name">
                          {language === "cn" ? (item.itemnamecn || item.itemname) : item.itemname}
                        </span>
                        <span className="order-item-qty-badge">× {item.quantity}</span>
                      </div>
                      <span className="order-item-price">Rs. {(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
              {confirmedOrders.length > 0 && (
                <div className="order-total-section">
                  <div className="order-total-row grand-total">
                    <span>{language === "cn" ? "Confirmed Total" : "已确认合计"}</span>
                    <span>Rs. {confirmedTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      
      {zoomedImage && (
        <div className="image-zoom-overlay" onClick={closeZoomedImage}>
          <div className="image-zoom-container" onClick={(e) => e.stopPropagation()}>
            <button className="image-zoom-close" onClick={closeZoomedImage}>✕</button>
            <img src={zoomedImage.url} alt={zoomedImage.name} className="image-zoom-image" />
            <div className="image-zoom-name">{zoomedImage.name}</div>
          </div>
        </div>
      )}

      
      {showInstructionsModal && (
        <div className="instructions-modal-overlay" onClick={handleInstructionsCancel}>
          <div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="instructions-modal-header">
              <h3>{language === "cn" ? "Special Instructions" : "备注"}</h3>
              <button className="instructions-modal-close" onClick={handleInstructionsCancel}>✕</button>
            </div>
            <div className="instructions-modal-body">
              <textarea
                className="instructions-textarea"
                value={tempInstructions}
                onChange={(e) => setTempInstructions(e.target.value)}
                placeholder={language === "cn" ? "Enter any special instructions..." : "请输入订单的备注..."}
                rows="4"
                maxLength={200}
              />
              <div className="instructions-char-count">{tempInstructions.length}/200</div>
            </div>
            <div className="instructions-modal-footer">
              <button className="instructions-cancel-btn" onClick={handleInstructionsCancel}>
                {language === "cn" ? "Cancel" : "取消"}
              </button>
              <button className="instructions-save-btn" onClick={handleInstructionsSave}>
                {language === "cn" ? "Save" : "保存"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}